import React from "react";
import { Paperclip, X, Download, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const MAX_BYTES = 25 * 1024 * 1024;

function bytesToLabel(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Compose absolute download URL. Uploads live at /api/uploads/:id.
function absUrl(rel) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return rel.startsWith("http") ? rel : `${base}${rel}`;
}

/**
 * Multi-file attachment picker.
 * value: [{id, url, filename, size, mime}]
 * onChange: (next) => void
 */
export function AttachmentPicker({ value = [], onChange, testid = "attachment-picker" }) {
    const [busy, setBusy] = React.useState(false);
    const inputRef = React.useRef(null);

    async function handleFiles(list) {
        setBusy(true);
        try {
            const uploaded = [];
            for (const file of list) {
                if (file.size > MAX_BYTES) {
                    toast.error(`${file.name} exceeds 25 MB`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploaded.push(data);
            }
            onChange([...(value || []), ...uploaded]);
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    }

    function remove(idx) {
        const next = [...value];
        next.splice(idx, 1);
        onChange(next);
    }

    return (
        <div className="space-y-2" data-testid={testid}>
            <input
                ref={inputRef}
                type="file"
                multiple
                data-testid={`${testid}-input`}
                onChange={e => e.target.files && handleFiles(Array.from(e.target.files))}
                className="hidden"
            />
            <div className="flex items-center gap-2 flex-wrap">
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    data-testid={`${testid}-btn`}
                    onClick={() => inputRef.current?.click()}
                    className="gap-1.5"
                >
                    <Paperclip className="w-3.5 h-3.5" />
                    {busy ? "Uploading…" : "Attach file"}
                </Button>
                <span className="text-[11px] text-brand-stone italic">Up to 25 MB each</span>
            </div>
            {value?.length > 0 && (
                <div className="space-y-1.5">
                    {value.map((att, i) => (
                        <div
                            key={att.id || i}
                            className="flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-1.5 text-sm bg-secondary/40"
                            data-testid={`${testid}-item-${i}`}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                {(att.mime || "").startsWith("image/") ? (
                                    <ImageIcon className="w-3.5 h-3.5 text-primary shrink-0" />
                                ) : (
                                    <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                                )}
                                <span className="truncate">{att.filename}</span>
                                <span className="text-[11px] text-brand-stone shrink-0">({bytesToLabel(att.size)})</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                className="text-brand-stone hover:text-destructive"
                                data-testid={`${testid}-remove-${i}`}
                                aria-label="Remove attachment"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Read-only list of attachments with download/preview buttons. */
export function AttachmentList({ items = [], testid = "attachment-list" }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="mt-3 space-y-1.5" data-testid={testid}>
            {items.map((a, i) => {
                const url = absUrl(a.url || `/api/uploads/${a.id}`);
                const isImage = (a.mime || "").startsWith("image/");
                return (
                    <div
                        key={a.id || i}
                        className="flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-2 text-sm bg-white"
                        data-testid={`${testid}-item-${i}`}
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            {isImage ? (
                                <ImageIcon className="w-4 h-4 text-primary shrink-0" />
                            ) : (
                                <FileText className="w-4 h-4 text-primary shrink-0" />
                            )}
                            <span className="truncate font-medium">{a.filename}</span>
                            {a.size ? (
                                <span className="text-[11px] text-brand-stone shrink-0">
                                    ({bytesToLabel(a.size)})
                                </span>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-1">
                            {isImage && (
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-primary underline"
                                    data-testid={`${testid}-preview-${i}`}
                                >
                                    Preview
                                </a>
                            )}
                            <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                download={a.filename}
                                className="text-brand-stone hover:text-primary"
                                data-testid={`${testid}-download-${i}`}
                                aria-label="Download"
                            >
                                <Download className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
